import { Head, Link, router } from '@inertiajs/react'
import { useState } from 'react'
import { ArrowLeft, Copy, RefreshCw, Shield, Globe, Check } from 'lucide-react'
import NavigationRail from '@/components/NavigationRail'
import SettingsNav from '@/components/SettingsNav'
import type { Agent } from '@/types/shared'

interface SecurityProps {
  currentAgent: Agent
  identityVerification: {
    enabled: boolean
    secret: string | null
  }
  allowedDomains: string[]
}

export default function Security({ currentAgent, identityVerification, allowedDomains }: SecurityProps) {
  const [ivEnabled, setIvEnabled] = useState(identityVerification.enabled)
  const [copied, setCopied] = useState(false)
  const [domains, setDomains] = useState(allowedDomains.join('\n'))
  const [saving, setSaving] = useState(false)

  const copySecret = () => {
    if (identityVerification.secret) {
      navigator.clipboard.writeText(identityVerification.secret)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const generateSecret = () => {
    if (confirm('Gerar uma nova chave vai invalidar todas as assinaturas existentes. Continuar?')) {
      router.post('/settings/security/generate_secret')
    }
  }

  const toggleVerification = () => {
    const newValue = !ivEnabled
    if (newValue && !identityVerification.secret) {
      alert('Gere uma chave secreta antes de habilitar a verificação de identidade.')
      return
    }
    setIvEnabled(newValue)
    router.patch('/settings/security', {
      section: 'identity_verification',
      identity_verification_enabled: newValue,
    })
  }

  const saveDomains = () => {
    setSaving(true)
    router.patch('/settings/security', {
      section: 'allowed_domains',
      allowed_domains: domains,
    }, {
      onFinish: () => setSaving(false),
    })
  }

  const maskedSecret = identityVerification.secret
    ? identityVerification.secret.slice(0, 8) + '••••••••••••••••' + identityVerification.secret.slice(-8)
    : null

  return (
    <>
      <Head title="Segurança" />
      <div className="h-screen flex bg-gray-50">
        <NavigationRail currentAgent={currentAgent} activePage="settings" />

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 bg-white border-b border-gray-200 flex items-center px-6">
            <Link
              href="/inbox"
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm mr-4"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <h1 className="text-lg font-semibold text-gray-900">Configurações</h1>
          </header>

          <div className="flex-1 flex min-h-0">
            <SettingsNav activePage="security" isAdmin={currentAgent.role === 'admin'} />

            <main className="flex-1 overflow-auto p-6">
            <div className="max-w-2xl">

            {/* Identity Verification Section */}
            <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-5 h-5 text-gray-700" />
                <h2 className="text-lg font-semibold text-gray-900">Verificação de Identidade</h2>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                A verificação de identidade impede que usuários mal-intencionados se passem por outros.
                Quando habilitada, o widget requer um <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">user_hash</code> HMAC-SHA256
                gerado no seu servidor para identificar usuários.
              </p>

              {/* Secret Key */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">Chave Secreta</label>
                {maskedSecret ? (
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-gray-700">
                      {maskedSecret}
                    </code>
                    <button
                      onClick={copySecret}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Copiado' : 'Copiar'}
                    </button>
                    <button
                      onClick={generateSecret}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Regenerar
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={generateSecret}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Gerar Chave Secreta
                  </button>
                )}
              </div>

              {/* Toggle */}
              <div className="flex items-center justify-between py-3 border-t border-gray-100">
                <div>
                  <p className="text-sm font-medium text-gray-900">Exigir verificação de identidade</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Usuários identificados sem <code className="bg-gray-100 px-1 rounded">user_hash</code> válido serão rejeitados
                  </p>
                </div>
                <button
                  onClick={toggleVerification}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    ivEnabled ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      ivEnabled ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </div>

              {/* Code Examples */}
              {identityVerification.secret && (
                <div className="mt-5 pt-5 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-3">Exemplo de integração</p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Ruby</p>
                      <pre className="text-xs bg-gray-900 text-green-400 rounded-lg p-3 overflow-x-auto">
{`OpenSSL::HMAC.hexdigest('SHA256', 'SUA_CHAVE_SECRETA', user.id.to_s)`}
                      </pre>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Node.js</p>
                      <pre className="text-xs bg-gray-900 text-green-400 rounded-lg p-3 overflow-x-auto">
{`crypto.createHmac('sha256', 'SUA_CHAVE_SECRETA').update(userId).digest('hex')`}
                      </pre>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Python</p>
                      <pre className="text-xs bg-gray-900 text-green-400 rounded-lg p-3 overflow-x-auto">
{`hmac.new(b'SUA_CHAVE_SECRETA', user_id.encode(), hashlib.sha256).hexdigest()`}
                      </pre>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-1">Widget</p>
                      <pre className="text-xs bg-gray-900 text-green-400 rounded-lg p-3 overflow-x-auto">
{`Signal.boot({
  appId: 'seu_app_id',
  userId: '123',
  email: 'usuario@exemplo.com',
  userHash: '<hash_gerado_no_servidor>'
})`}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Allowed Domains Section */}
            <section className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="w-5 h-5 text-gray-700" />
                <h2 className="text-lg font-semibold text-gray-900">Domínios Permitidos</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Restrinja em quais domínios o widget pode ser embarcado. Quando configurado,
                o widget só funcionará nos domínios listados. Deixe vazio para permitir qualquer domínio.
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Domínios (um por linha)
                </label>
                <textarea
                  value={domains}
                  onChange={(e) => setDomains(e.target.value)}
                  rows={5}
                  placeholder={`exemplo.com\n*.exemplo.com\napp.meusite.com`}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 font-mono focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1.5">
                  Use <code className="bg-gray-100 px-1 rounded">*.exemplo.com</code> para permitir todos os subdomínios
                </p>
              </div>

              <button
                onClick={saveDomains}
                disabled={saving}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50"
              >
                {saving ? 'Salvando...' : 'Salvar Domínios'}
              </button>
            </section>
          </div>
            </main>
          </div>
        </div>
      </div>
    </>
  )
}
